import React, { useEffect, useRef, useState } from 'react';
import { useVersion } from '../contexts/VersionContext';
import { clarityEvent, gaEvent } from '../analytics';
import useSectionViewTracking from '../hooks/useSectionViewTracking';
import styles from './WhatsInTheBox.module.css';
import ImageZoomMagnifier from './ImageZoomMagnifier';

/** Box carousel images: /public/images/Contents-*.jpg (alphabetical by filename). */
const components = [
  {
    id: 1,
    name: 'Civilizational Traits',
    image: '/images/webp/Contents-1-Traits.webp',
  },
  {
    id: 2,
    name: 'Humble Beginnings',
    image: '/images/webp/Contents-2-HB.webp',
  },
  {
    id: 3,
    name: 'Resource Cards',
    image: '/images/webp/Contents-3-Resources.webp',
  },
  {
    id: 4,
    name: 'Setup',
    image: '/images/webp/Contents-4-Setup.webp',
  },
  {
    id: 5,
    name: 'Government Effects',
    image: '/images/webp/Contents-5-Effects.webp',
  },
  {
    id: 6,
    name: 'Tech Board',
    image: '/images/webp/Contents-6-Techboard.webp',
  },
];

const WhatsInTheBox = ({ visualFirst = false }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { isImprovedVersion } = useVersion();
  const boxRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const whatsInTheBoxRef = useSectionViewTracking('whats_in_the_box');
  const boxContentsListRef = useSectionViewTracking('whats_in_the_box', {
    threshold: 1,
    onView: () => {
      gaEvent('view_box_contents', { section_name: 'box_contents' });
      clarityEvent('view_box_contents');
    },
  });
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.15 }
    );

    const element = boxRef.current;
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, []);

  // Old component groups structure
  /*
  const componentGroups = [
    {
      title: 'Core Boards',
      items: [
        '1 Tech Board',
        '4 City Boards'
      ]
    },
    {
      title: 'Cards & Systems',
      items: [
        '400+ Resource Cards',
        '7 Humble Beginnings Cards',
        '8 Civilizational Traits Cards',
        '3 Government Effect Cards'
      ]
    },
    {
      title: 'Tiles & Wonders',
      items: [
        '88 Building Tiles',
        '14 Wonder Tiles'
      ]
    },
    {
      title: 'Rules & Reference',
      items: [
        '1 Rule Book',
        '4 Cost Cards',
        '2 Special Cost Cards',
        '20 Turn Planner Sheets'
      ]
    }
  ];

  const leftGroups = componentGroups.slice(0, 2);
  const rightGroups = componentGroups.slice(2);
  */

  // New component groups structure
  const componentGroups = [
    {
      title: 'Game Components',
      items: [
        '1 Rulebook',
        '1 Technology Board',
        '4 City Boards',
        '4 Cost Cards',
        '2 Special Cost Cards',
        '400 Resource Cards',
        '8 Trait Cards',
        '7 Humble Beginnings Cards',
        '3 Government Extra Benefit Cards',
        '100 Population Tokens',
        '14 Wonder Tiles',
        '88 Building Tiles',
        '40 Resource Trackers Tokens',
        '180 Research Tracker Tokens',
        '1 Pair of Dice',
        '20 Turn Planner Pages'
      ]
    }
  ];

  const leftGroups = componentGroups;
  const rightGroups = [];

  const goToNext = () => {
    const nextIndex = (currentIndex + 1) % components.length;
    gaEvent('carousel_interaction', {
      carousel_name: 'box_contents_carousel',
      action: 'next',
      slide_index: nextIndex,
      slide_name: components[nextIndex].name,
    });
    clarityEvent('carousel_next');
    setCurrentIndex(nextIndex);
  };

  const goToPrevious = () => {
    const prevIndex = currentIndex === 0 ? components.length - 1 : currentIndex - 1;
    gaEvent('carousel_interaction', {
      carousel_name: 'box_contents_carousel',
      action: 'prev',
      slide_index: prevIndex,
      slide_name: components[prevIndex].name,
    });
    clarityEvent('carousel_prev');
    setCurrentIndex(prevIndex);
  };
  
  const handleDotClick = (index) => {
    gaEvent('carousel_interaction', {
      carousel_name: 'box_contents_carousel',
      action: 'dot_click',
      slide_index: index,
      slide_name: components[index].name,
    });
    clarityEvent('carousel_dot_click');
    setCurrentIndex(index);
  };

  const handleTouchStart = (event) => {
    const touch = event.touches[0];
    touchStartX.current = touch.clientX;
    touchStartY.current = touch.clientY;
  };

  const handleTouchEnd = (event) => {
    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - touchStartX.current;
    const deltaY = touch.clientY - touchStartY.current;
    const swipeThreshold = 50;

    if (Math.abs(deltaX) < swipeThreshold || Math.abs(deltaX) <= Math.abs(deltaY)) {
      return;
    }

    if (deltaX < 0) {
      goToNext();
    } else {
      goToPrevious();
    }
  };

  return (
    <section
      ref={whatsInTheBoxRef}
      className={`${styles.whatsInTheBox} ${isImprovedVersion ? styles.whatsInTheBoxImproved : ''}`}
      data-wib-theme="parchment"
      id="components"
    >
      <div className={styles.container}>
        <h2 className={styles.heading}>What's in the Box</h2>
        <div 
          ref={boxRef}
          className={`${styles.boxContainer} ${isVisible ? styles.boxVisible : ''} ${visualFirst ? styles.visualFirst : ''}`}
        >
          <div className={styles.content}>
          {/* List Section */}
          <div ref={boxContentsListRef} className={styles.listSection}>
            <div className={styles.componentColumns}>
              <div className={styles.componentColumn}>
                {leftGroups.map((group, groupIndex) => (
                  <div key={`left-${groupIndex}`} className={styles.componentGroup}>
                    <h3 className={styles.groupTitle}>{group.title}</h3>
                    <ul className={styles.componentsList}>
                      {group.items.map((item, itemIndex) => (
                        <li key={itemIndex} className={styles.listItem}>
                          <span className={styles.bullet}>•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              <div className={styles.componentColumn}>
                {rightGroups.map((group, groupIndex) => (
                  <div key={`right-${groupIndex}`} className={styles.componentGroup}>
                    <h3 className={styles.groupTitle}>
                      {group.title}
                      {group.subtitle && <span className={styles.groupSubtitle}>{group.subtitle}</span>}
                    </h3>
                    <ul className={styles.componentsList}>
                      {group.items.map((item, itemIndex) => (
                        <li key={itemIndex} className={styles.listItem}>
                          <span className={styles.bullet}>•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>

            <div className={styles.carouselSection}>
            <div className={styles.carouselImageArea}>
              <div
                className={styles.imageContainer}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
              >
                <ImageZoomMagnifier
                  key={currentIndex}
                  src={components[currentIndex].image}
                  alt={components[currentIndex].name}
                  imageClassName={styles.componentImage}
                  wrapperClassName={styles.componentImageWrapper}
                  enableTouch={false}
                  onZoomActivate={() => {
                    gaEvent('carousel_zoom', {
                      carousel_name: 'box_contents_carousel',
                      slide_index: currentIndex,
                      slide_name: components[currentIndex].name,
                    });
                    clarityEvent('carousel_zoom');
                  }}
                />
              </div>
            </div>
            <div className={styles.carouselControls}>
              <button
                type="button"
                className={`${styles.arrowBtn} ${styles.carouselArrowDesktop}`}
                onClick={goToPrevious}
                aria-label="Previous component"
              >
                <span className={styles.arrowIcon} aria-hidden="true">‹</span>
              </button>
              <div className={styles.carouselDots} role="presentation">
                {components.map((_, index) => (
                  <button
                    type="button"
                    key={index}
                    className={`${styles.dot} ${index === currentIndex ? styles.activeDot : ''}`}
                    onClick={() => handleDotClick(index)}
                    aria-label={`Go to component ${index + 1}`}
                  />
                ))}
              </div>
              <button
                type="button"
                className={`${styles.arrowBtn} ${styles.carouselArrowDesktop}`}
                onClick={goToNext}
                aria-label="Next component"
              >
                <span className={styles.arrowIcon} aria-hidden="true">›</span>
              </button>
            </div>
          </div>
        </div>

        {/*
        <div className={styles.safetySection}>
          <h3 className={styles.safetyTitle}>Safety & Compliance</h3>
          <p className={styles.safetyText}>
            Manufactured and tested to meet international safety standards.
          </p>
          <p className={styles.safetyText}>
            INCEPTUM: Dawn of Civilizations is certified under EN71, BIS, and ASTM, and is approved for sale in India, the US, the UK, and the EU.
          </p>
        </div>
        */}
        
        </div>
      </div>
    </section>
  );
};

export default WhatsInTheBox;
